import { previousDirectionRecord } from '@src/services/dubbing/directionPrefill';

import { providerLabel } from './voiceLabels';

import type { TooltipEntry } from '@src/components/common/ClipContextTooltip';
import type {
  DubDirectionRecordDTO,
  DubManifestDTO,
  DubManifestSegment,
} from '@src/repositories/DubbingRepository';

/**
 * Build the key/value rows shown in the green-mic dub-info overlay (PLAT-10732)
 * from a resolved dub manifest. Pure — the caller injects the resolvers
 * (`DubbingStore` voice lookups, a date formatter, a language caption) so this
 * stays unit-testable without stores or Intl locale flakiness.
 *
 * Who / when / language / speed come straight off the manifest; voice names +
 * vendors are resolved per distinct voice used across the active segments (a dub
 * can use several voices — one per speaker).
 */
export interface DubInfoDeps {
  /** `aiVoiceId` → display name; `undefined` when the catalog can't resolve it. */
  voiceName: (aiVoiceId: string) => string | undefined;
  /** `aiVoiceId` → provider/vendor key (e.g. `elevenlabs`); fallback for older
   * manifests that don't carry a per-segment `handler`. */
  voiceProvider: (aiVoiceId: string) => string | undefined;
  /** Format an ISO timestamp for display; `null` when unavailable. */
  formatDate: (iso: string) => string | null;
  /** BCP-47 code → human caption. */
  languageCaption: (code: string) => string;
}

/**
 * Rows for the green-mic dub-info overlay, split so the overlay can keep the
 * (rare, potentially long) per-segment sped-up list collapsed behind a caret
 * and only expand it on click (PLAT-10732).
 */
export interface DubInfoContent {
  /** Always-shown identity rows: who/when/voice(s)/vendor(s). The language sits
   * in the overlay's top (title) row, not an entry, so it's carried separately. */
  entries: TooltipEntry[];
  /** Target-language caption, shown across the overlay's "Dub Info" title row. */
  language: string;
  /** The one global change: the uniform speed-up applied to the whole dub
   * (`"1.50×"`), or `null` when there was none — shown only when set. */
  fullDubSpeedUp: string | null;
  /** Collapsible detail: the segments that slid past their original start
   * (timecode + snippet), one row each. Empty when nothing slid. */
  slidSegments: TooltipEntry[];
  /** Collapsible detail: the individually sped-up segments (timecode + snippet),
   * one row each. Empty in the normal case (nothing sped up individually). */
  spedUpSegments: TooltipEntry[];
  /**
   * PLAT-11799 — what this take was asked for, how each voice model resolved
   * it, and what each line actually measured. `null` when the dub was never
   * directed, which is every dub made before directing existed.
   */
  direction: DubDirectionContent | null;
}

/**
 * The direction part of the overlay (PLAT-11799).
 *
 * Three separate things, deliberately not run together: what a person ASKED
 * for, what each voice model actually DID with it, and what the recording then
 * MEASURED. They can disagree, and the whole point of showing them is that a
 * person can see where they disagree.
 */
export interface DubDirectionContent {
  /** One row: the delivery that was asked for, in the person's own words. */
  asked: TooltipEntry[];
  /** One row per voice model in the run: what it did with that direction. */
  resolved: TooltipEntry[];
  /**
   * One row per dubbed line that carries measurements: its time, its words, and
   * a short note saying how fast it was read and whether it is still with the
   * picture. Collapsible — a long story has a lot of lines.
   */
  perLine: TooltipEntry[];
}

export function buildDubInfoEntries(
  manifest: DubManifestDTO,
  deps: DubInfoDeps,
): DubInfoContent {
  const prov = manifest.modifiedBy ?? manifest.createdBy;
  const dubbedBy = prov?.name ?? prov?.email ?? 'Unknown';
  const when = manifest.modifiedAt ?? manifest.createdAt;
  const dubbedOn = (when && deps.formatDate(when)) || 'Unknown';

  // Distinct voices + vendors, in first-seen segment order.
  const voiceIds: string[] = [];
  const providers: string[] = [];
  for (const seg of manifest.segments) {
    if (seg.aiVoiceId && !voiceIds.includes(seg.aiVoiceId)) {
      voiceIds.push(seg.aiVoiceId);
    }
    const provider =
      seg.handler?.provider ??
      (seg.aiVoiceId ? deps.voiceProvider(seg.aiVoiceId) : undefined);
    if (provider && !providers.includes(provider)) providers.push(provider);
  }
  const voiceNames = voiceIds.map(id => deps.voiceName(id) ?? id);
  const vendorNames = providers.map(providerLabel);

  const entries: TooltipEntry[] = [
    { label: 'Dubbed by', value: dubbedBy },
    { label: 'Dubbed on', value: dubbedOn },
  ];
  if (voiceNames.length) {
    entries.push({
      label: voiceNames.length > 1 ? 'Voices' : 'Voice',
      value: voiceNames.join(', '),
    });
  }
  if (vendorNames.length) {
    entries.push({
      label: vendorNames.length > 1 ? 'Vendors' : 'Vendor',
      value: vendorNames.join(', '),
    });
  }
  // The Forced Fit "Adjustments to fit voice in timeline" section is assembled
  // by the overlay from these three pieces: the global speed-up value plus the
  // two per-segment lists (kept collapsible behind a caret).
  return {
    entries,
    language: deps.languageCaption(manifest.targetLanguage),
    fullDubSpeedUp: fullDubSpeedUpLabel(manifest),
    slidSegments: slidSegmentRows(manifest),
    spedUpSegments: spedUpSegmentRows(manifest),
    direction: directionContent(manifest),
  };
}

/**
 * The direction block of the overlay, or `null` for a dub that was never
 * directed (PLAT-11799).
 */
function directionContent(
  manifest: DubManifestDTO,
): DubDirectionContent | null {
  const record = previousDirectionRecord(manifest);
  const perLine = perLineOutcomeRows(manifest);
  if (!record) return null;
  return {
    asked: [askedRow(record)],
    resolved: resolvedRows(record),
    perLine,
  };
}

/** What was asked for, said in the form it was asked in. */
function askedRow(record: DubDirectionRecordDTO): TooltipEntry {
  const direction = record.direction;
  switch (direction.kind) {
    case 'free-form':
      return { label: 'Asked for', value: snippet(direction.note, 120) };
    case 'tags':
      return {
        label: 'Asked for',
        value: snippet(direction.taggedText, 120),
        note: 'delivery markers',
      };
    case 'tags-by-line':
      return {
        label: 'Asked for',
        value: snippet(
          Object.values(direction.taggedTextByLine).join(' '),
          120,
        ),
        note: 'delivery markers',
      };
    case 'config': {
      const pairs = Object.entries(direction.settings)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([name, value]) => `${name} ${String(value)}`);
      return { label: 'Asked for', value: pairs.join(', ') || '—' };
    }
  }
}

/**
 * One row per voice model: which model took the direction, and what it actually
 * set. A model that expressed nothing says so rather than being left out — an
 * absent row would read as "it worked".
 */
function resolvedRows(record: DubDirectionRecordDTO): TooltipEntry[] {
  return record.interpretation.map(entry => {
    const applied: string[] = [];
    if (entry.applied.native) {
      applied.push(`sent as ${entry.applied.native.parameter}`);
    }
    if (entry.applied.settings) {
      const pairs = Object.entries(entry.applied.settings)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([name, value]) => `${name} ${String(value)}`);
      if (pairs.length) applied.push(pairs.join(', '));
    }
    if (entry.applied.tags) applied.push('sent as delivery markers');
    const row: TooltipEntry = {
      label: entry.resolvedModelName,
      value: applied.length ? applied.join('; ') : 'nothing was applied',
    };
    // Named plainly rather than dropped: a part of the direction the model
    // could not express is the thing a person most needs to know.
    return entry.unexpressed.length > 0
      ? { ...row, note: `not expressed: ${entry.unexpressed.join(', ')}` }
      : row;
  });
}

/**
 * One row per line the backend measured: where it sits, what it says, and a
 * short note about how it came out.
 *
 * Independent of whether the run was directed — the measurements are taken on
 * every run — but shown beside the direction because "did the delivery I asked
 * for fit?" is the question they answer.
 */
function perLineOutcomeRows(manifest: DubManifestDTO): TooltipEntry[] {
  return manifest.segments
    .filter(segment => segment.diagnostics)
    .slice()
    .sort((a, b) => a.originalStartMs - b.originalStartMs)
    .map(segment => {
      const note = outcomeNote(segment);
      const row: TooltipEntry = {
        label: formatTimecode(segment.placedStartMs ?? segment.originalStartMs),
        value: snippet(segment.sourceTranslationSnapshot),
      };
      return note ? { ...row, note } : row;
    });
}

/**
 * The short "how did this line come out" note: how much faster than natural it
 * was read, whether any of it was cut off, and whether it is still with the
 * picture.
 *
 * "Late" and "early" are about the PICTURE, not about a schedule: a line that
 * ends after its own piece of video has moved on is late.
 */
function outcomeNote(segment: DubManifestSegment): string | undefined {
  const d = segment.diagnostics;
  if (!d) return undefined;
  const parts: string[] = [];
  if (
    typeof d.achievedSpeedFactor === 'number' &&
    d.achievedSpeedFactor > 1.005
  ) {
    parts.push(`${d.achievedSpeedFactor.toFixed(2)}× faster`);
  }
  if (typeof d.truncatedMs === 'number' && d.truncatedMs > 0) {
    parts.push(`${(d.truncatedMs / 1000).toFixed(2)}s cut off the end`);
  }
  if (typeof d.endLagMs === 'number') {
    const seconds = Math.abs(d.endLagMs) / 1000;
    if (seconds >= 0.05) {
      parts.push(
        d.endLagMs > 0
          ? `${seconds.toFixed(2)}s past the picture`
          : `${seconds.toFixed(2)}s inside the picture`,
      );
    } else {
      parts.push('with the picture');
    }
  }
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

/**
 * The "Full Dub Speed-up" value: the one global change — a uniform speed-up
 * applied to the whole dub (`N×`), or `null` when none was applied (or the dub
 * predates alignment tracking) so the overlay can omit the row entirely. The
 * per-segment slid / sped-up lists are surfaced as their own collapsible rows.
 */
function fullDubSpeedUpLabel(manifest: DubManifestDTO): string | null {
  const a = manifest.alignment;
  if (!a || a.globalSpeedFactor <= 1) return null;
  return `${formatSpeedFactor(a.globalSpeedFactor)}×`;
}

/**
 * One row per segment that slid past its own original start (drift), each as its
 * timestamp (label) + a short text snippet (value), sorted by time. Prefers the
 * engine-recorded `slidSegmentIds`; falls back to deriving from placement for
 * manifests written before it was tracked. Empty when nothing slid.
 */
function slidSegmentRows(manifest: DubManifestDTO): TooltipEntry[] {
  const align = manifest.alignment;
  if (!align) return [];
  let segs: DubManifestSegment[];
  if (align.slidSegmentIds) {
    const byId = new Map(manifest.segments.map(s => [s.segmentId, s]));
    segs = align.slidSegmentIds
      .map(id => byId.get(id))
      .filter((s): s is DubManifestSegment => s != null);
  } else {
    segs = manifest.segments.filter(
      s => s.placedStartMs != null && s.placedStartMs > s.originalStartMs,
    );
  }
  // Show the NEW (placed) start on the left so a slid line is easy to locate in
  // context, with its shift amount (how far it moved past its own start) as a
  // muted trailing note. Falls back to the original start when placement is
  // unrecorded (PLAT-10732).
  return segs
    .slice()
    .sort((a, b) => a.originalStartMs - b.originalStartMs)
    .map(s => {
      const shift =
        s.placedStartMs != null ? s.placedStartMs - s.originalStartMs : 0;
      const startMs = s.placedStartMs ?? s.originalStartMs;
      const row: TooltipEntry = {
        label: formatTimecode(startMs),
        value: snippet(s.sourceTranslationSnapshot),
      };
      return shift > 0 ? { ...row, note: formatShift(shift) } : row;
    });
}

/**
 * One row per segment the last-resort pass sped up (`shrunkSegmentIds`), each as
 * its timestamp (label) + a short text snippet (value), sorted by time. Empty
 * when nothing was sped up individually — the normal case, since Slide + a
 * global speed-up is expected to fit.
 */
function spedUpSegmentRows(manifest: DubManifestDTO): TooltipEntry[] {
  const ids = manifest.alignment?.shrunkSegmentIds ?? [];
  if (ids.length === 0) return [];
  const byId = new Map(manifest.segments.map(s => [s.segmentId, s]));
  return toSegmentRows(
    ids
      .map(id => byId.get(id))
      .filter((s): s is DubManifestSegment => s != null),
  );
}

/** Segments → tooltip rows (timecode label + text snippet), sorted by time. */
function toSegmentRows(segments: DubManifestSegment[]): TooltipEntry[] {
  return segments
    .slice()
    .sort((a, b) => a.originalStartMs - b.originalStartMs)
    .map(s => ({
      label: formatTimecode(s.originalStartMs),
      value: snippet(s.sourceTranslationSnapshot),
    }));
}

/** A slide distance in ms → a compact signed seconds note, two decimals so a
 * sub-100ms slip still reads as a real shift rather than `+0.0s` (`"+0.04s"`). */
function formatShift(ms: number): string {
  return `+${(ms / 1000).toFixed(2)}s`;
}

/** `ms` → `m:ss` timecode (no hours — dub segments are short). */
function formatTimecode(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** Collapse whitespace and trim a segment's text to a tooltip-sized snippet.
 * The row itself is single-line + CSS-ellipsized, so this is just a generous
 * upper bound to avoid shipping a whole paragraph into the DOM. */
function snippet(text: string, max = 80): string {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;
}

/**
 * Global speed-up factor for display. Two decimals by default (`1.50`), but a
 * sub-1% speed-up rounds to `1.00` at two decimals — which reads as "no change"
 * next to a "Yes". When that happens, show four decimals so the exact ratio is
 * visible (`1.0034`). The caller only reaches here when the factor is > 1.
 */
function formatSpeedFactor(factor: number): string {
  const twoDp = factor.toFixed(2);
  return twoDp === '1.00' ? factor.toFixed(4) : twoDp;
}
